'use client'

import { useMemo } from 'react'

import { CATALOG_DISABLED_EXCHANGES } from '@/lib/configs/exchange-registry'
import { getInstrumentSymbolMap } from '@/lib/utils/exchange-instrument-map'
import { useInstrumentList } from '@/service/instrument.hook'
import { useExchangesEnabled } from '@/service/venue.hook'
import { Exchange, Instrument } from '@/types/generated/common/types'

const EMPTY_INSTRUMENTS: Instrument[] = []

// Binance is currently unsupported (no instrument catalog wired up in any
// environment yet) — deliberately excluded rather than left to the
// enabled-flag gate below, so this hook never fires that request at all, not
// even the one-shot fetch that'd otherwise happen while GetVenues is still
// loading (isTradeVenueEnabled defaults enabled=true until venues data
// arrives). Remove this line once Binance actually has an instrument catalog
// to serve.
export const CRYPTO_EXCHANGES: Exchange[] = [
  Exchange.EXCHANGE_DERIBIT,
  Exchange.EXCHANGE_BYBIT,
  Exchange.EXCHANGE_HYPERLIQUID,
  Exchange.EXCHANGE_DERIVE,
  Exchange.EXCHANGE_UPBIT,
  Exchange.EXCHANGE_LIGHTER,
  Exchange.EXCHANGE_PAPERTRADE,
]

// Every exchange this map resolves instruments for. KRX equities reach us via
// the Toss venue and are emphatically not crypto, so they are appended here
// rather than added to CRYPTO_EXCHANGES — the spreader picks its hedge legs off
// that list in priority order (features/spreader/lib/spreader-seed.ts) and must
// never consider a Korean equity market. Positions and orders on a Toss account
// still resolve their instrument through this map like any other, so leaving
// XKRX out entirely would leave those rows unresolved.
const MAPPED_EXCHANGES: Exchange[] = [...CRYPTO_EXCHANGES, Exchange.EXCHANGE_XKRX]

/**
 * Symbol→Instrument lookup per exchange, for call sites that must genuinely
 * compare/aggregate data across multiple exchanges at once (e.g. resolving
 * positions that may live on any of the user's connected accounts).
 * Single-exchange call sites should use `useInstrumentMap(exchange)`
 * (`features/order/order-form/hooks/use-instrument-map.ts`) instead — routing
 * a single-exchange lookup through this hook would spin up every query observer
 * just to use one. Always fetches every mapped exchange's list (long-cached,
 * reused across the app) rather than only the user's connected ones, so hook
 * call count stays fixed.
 */
export function useMultiExchangeInstrumentMap(): Map<Exchange, Map<string, Instrument>> {
  return useMultiExchangeInstrumentMapState().map
}

/**
 * useMultiExchangeInstrumentMap plus which exchanges' catalogs are still on their first fetch.
 * Every mapped exchange is in the map from the start (an empty sub-map until its catalog
 * lands), so `map.has(exchange)` can't say whether a lookup is ready yet — this can. An exchange
 * that isn't mapped at all, or whose fetch is disabled or settled, is never pending.
 */
export function useMultiExchangeInstrumentMapState(): {
  map: Map<Exchange, Map<string, Instrument>>
  pendingExchanges: ReadonlySet<Exchange>
} {
  // Keyed by exchange (not positional) so MAPPED_EXCHANGES can be reordered
  // without silently pairing the wrong exchange with the wrong enabled flag.
  const enabledByExchange = useExchangesEnabled(MAPPED_EXCHANGES)
  const deribit = useInstrumentList(
    Exchange.EXCHANGE_DERIBIT,
    enabledByExchange.get(Exchange.EXCHANGE_DERIBIT)
  )
  const bybit = useInstrumentList(
    Exchange.EXCHANGE_BYBIT,
    enabledByExchange.get(Exchange.EXCHANGE_BYBIT)
  )
  const hyperliquid = useInstrumentList(
    Exchange.EXCHANGE_HYPERLIQUID,
    enabledByExchange.get(Exchange.EXCHANGE_HYPERLIQUID)
  )
  const derive = useInstrumentList(
    Exchange.EXCHANGE_DERIVE,
    enabledByExchange.get(Exchange.EXCHANGE_DERIVE)
  )
  const upbit = useInstrumentList(
    Exchange.EXCHANGE_UPBIT,
    enabledByExchange.get(Exchange.EXCHANGE_UPBIT)
  )
  const lighter = useInstrumentList(
    Exchange.EXCHANGE_LIGHTER,
    enabledByExchange.get(Exchange.EXCHANGE_LIGHTER)
  )
  const papertrade = useInstrumentList(
    Exchange.EXCHANGE_PAPERTRADE,
    enabledByExchange.get(Exchange.EXCHANGE_PAPERTRADE)
  )
  // See CATALOG_DISABLED_EXCHANGES's doc comment — XKRX's backend catalog isn't
  // ready yet, so this AND's it in on top of the GetVenues gate below (the same
  // static override the wizard/dashboard catalog prefetch uses) rather than
  // repeating a guaranteed-error request. Left in MAPPED_EXCHANGES (unlike the
  // Binance exclusion above) so a Toss position/order row still resolves to an
  // empty-but-present sub-map, not a missing key.
  const xkrx = useInstrumentList(
    Exchange.EXCHANGE_XKRX,
    enabledByExchange.get(Exchange.EXCHANGE_XKRX) &&
      !CATALOG_DISABLED_EXCHANGES.has(Exchange.EXCHANGE_XKRX)
  )

  const byExchange = useMemo(
    () =>
      ({
        [Exchange.EXCHANGE_DERIBIT]: deribit.data?.instruments,
        [Exchange.EXCHANGE_BYBIT]: bybit.data?.instruments,
        [Exchange.EXCHANGE_HYPERLIQUID]: hyperliquid.data?.instruments,
        [Exchange.EXCHANGE_DERIVE]: derive.data?.instruments,
        [Exchange.EXCHANGE_UPBIT]: upbit.data?.instruments,
        [Exchange.EXCHANGE_LIGHTER]: lighter.data?.instruments,
        [Exchange.EXCHANGE_PAPERTRADE]: papertrade.data?.instruments,
        [Exchange.EXCHANGE_XKRX]: xkrx.data?.instruments,
      }) as Record<Exchange, Instrument[] | undefined>,
    [
      deribit.data,
      bybit.data,
      hyperliquid.data,
      derive.data,
      upbit.data,
      lighter.data,
      papertrade.data,
      xkrx.data,
    ]
  )

  const map = useMemo(() => {
    const map = new Map<Exchange, Map<string, Instrument>>()
    for (const exchange of MAPPED_EXCHANGES) {
      const instruments = byExchange[exchange] ?? EMPTY_INSTRUMENTS
      map.set(exchange, getInstrumentSymbolMap(instruments))
    }
    return map
  }, [byExchange])

  // First fetch in flight: no data yet and actually fetching (a disabled query is pending too,
  // but idle — nothing will ever arrive for it, so nothing should wait on it).
  const queryByExchange: Partial<Record<Exchange, { isPending: boolean; fetchStatus: string }>> = {
    [Exchange.EXCHANGE_DERIBIT]: deribit,
    [Exchange.EXCHANGE_BYBIT]: bybit,
    [Exchange.EXCHANGE_HYPERLIQUID]: hyperliquid,
    [Exchange.EXCHANGE_DERIVE]: derive,
    [Exchange.EXCHANGE_UPBIT]: upbit,
    [Exchange.EXCHANGE_LIGHTER]: lighter,
    [Exchange.EXCHANGE_PAPERTRADE]: papertrade,
    [Exchange.EXCHANGE_XKRX]: xkrx,
  }
  const pendingList = MAPPED_EXCHANGES.filter((exchange) => {
    const query = queryByExchange[exchange]
    return !!query && query.isPending && query.fetchStatus === 'fetching'
  })
  const pendingKey = pendingList.join(',')
  const pendingExchanges = useMemo(
    () => new Set(pendingList),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingKey captures pendingList
    [pendingKey]
  )

  return useMemo(() => ({ map, pendingExchanges }), [map, pendingExchanges])
}
